import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import {
  type ListRecordsParams,
  type RecordPage,
  ancestorsOf,
  childrenOf,
  countRecords,
  getRecord,
  getRecordForEdit,
  listRecords,
  listVersions,
  recentRecords,
} from "@/features/records/api";

/**
 * Keyset pagination, so an infinite query and never a page number.
 *
 * The API returns no `total` — counting defeats the keyset scan, which is
 * the trade PR2 recorded deliberately. "Page 7 of 12" is unbuildable and
 * load-more is the only honest control.
 *
 * Every filter is in the query key, including the free-text `q`. A key
 * that omitted one would serve the previous filter's rows while the new
 * request was in flight, which reads as the filter not working.
 */
export function recordsQuery(code: string, params: ListRecordsParams) {
  return infiniteQueryOptions({
    queryKey: ["records", code, params],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listRecords(code, { ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: RecordPage) => page.next_cursor ?? undefined,
  });
}

/**
 * One page of records, for the reference picker.
 *
 * A plain query rather than the infinite one above, deliberately: the
 * picker is capped and offers no "next page", so an infinite query would
 * carry pagination machinery it must never use — and `placeholderData`
 * keeping the previous *page* on screen is a different thing from
 * keeping the previous accumulated pages.
 */
export function recordSearchQuery(code: string, params: ListRecordsParams) {
  return queryOptions({
    queryKey: ["record-search", code, params],
    queryFn: () => listRecords(code, params),
  });
}

/** The dashboard's landing tile — one cheap COUNT, not a page of rows. */
export function recordCountQuery(code: string) {
  return queryOptions({
    queryKey: ["record-count", code],
    queryFn: () => countRecords(code),
    staleTime: 60 * 1000,
  });
}

/** One type's contribution to the dashboard's "recently updated" feed —
 *  the caller merges every readable type's results and re-sorts. */
export function recentRecordsQuery(code: string, locale: string, limit = 5) {
  return queryOptions({
    queryKey: ["record-recent", code, { locale, limit }],
    queryFn: () => recentRecords(code, { limit, locale }),
    staleTime: 60 * 1000,
  });
}

export function recordQuery(code: string, id: string, locale: string) {
  return queryOptions({
    queryKey: ["record", code, id, { locale }],
    queryFn: () => getRecord(code, id, locale),
  });
}

/**
 * The all-locales read an edit form loads, and the ETag its save needs.
 *
 * `staleTime: 0` and no `refetchOnWindowFocus`: refetching mid-edit would
 * swap the ETag under the form, so the first save after the user came
 * back from another tab would 409 for no reason they could see.
 */
export function recordEditQuery(code: string, id: string) {
  return queryOptions({
    queryKey: ["record-edit", code, id],
    queryFn: () => getRecordForEdit(code, id),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function recordVersionsQuery(code: string, id: string) {
  return queryOptions({
    queryKey: ["record-versions", code, id],
    queryFn: () => listVersions(code, id),
  });
}

/**
 * One level of the tree, fetched when a node is expanded.
 *
 * Lazily, and that is DEE-29's "thousands of siblings without freezing":
 * the tree never holds more than the levels somebody actually opened, and
 * the cache means collapsing and reopening costs nothing.
 */
export function childrenQuery(
  code: string,
  id: string,
  options: { locale: string; includeInactive: boolean },
) {
  return queryOptions({
    queryKey: ["record-children", code, id, options],
    queryFn: () => childrenOf(code, id, options),
  });
}

export function ancestorsQuery(code: string, id: string, locale: string) {
  return queryOptions({
    queryKey: ["record-ancestors", code, id, { locale }],
    queryFn: () => ancestorsOf(code, id, { locale }),
  });
}
